/**
 * The legal documents a consumer has to accept, and the record that they did.
 *
 * Three endpoints, and the split between them is the whole design:
 *
 * - `GET /v1/legal/documents/{kind}` is **public**. No bearer, no session — the text has to be
 *   readable from the registration form, from a shared link and from a phone that is signed out.
 * - `GET /v1/consumer/legal/pending` is the only source of truth for «did this person accept».
 *   Nothing about that answer is stored locally: a flag in localStorage is forgeable and proves
 *   nothing in a dispute, so the client asks the server every time it matters.
 * - `POST /v1/consumer/legal/acceptances` records the acceptance together with the
 *   `content_hash` of the text that was **shown**. That hash is what turns «он нажал кнопку» into
 *   «он нажал кнопку под вот этим текстом», which is the only version worth defending later.
 *
 * A request refused for want of an acceptance answers `legal_acceptance_required` — handled in
 * `shared/api/client.ts`, by code and not by status.
 */
import { apiRequest } from '@/shared/api/client';
import { APP_VERSION } from '@/shared/lib/appVersion';

/** The three documents the backend serves. Anything else is a 404 there and a 404 here. */
export const LEGAL_KINDS = [
  'offer_and_privacy_policy',
  'personal_data_processing',
  'order_checklist',
] as const;

export type LegalKind = (typeof LEGAL_KINDS)[number];

export function isLegalKind(value: string): value is LegalKind {
  return (LEGAL_KINDS as readonly string[]).includes(value);
}

export interface LegalDocument {
  kind: LegalKind;
  /** A date-like tag («2026-08-22»), not a number — never compared, only echoed back. */
  version: string;
  title: string;
  /** The full text. Rendered by `shared/lib/markdown`; never truncated, never summarised. */
  body_markdown: string;
  /** `sha256:…` of exactly this text. */
  content_hash: string;
  effective_from: string;
}

/** What `pending` carries per document — everything except the text itself. */
export interface PendingLegalDocument {
  kind: LegalKind;
  version: string;
  title: string;
  content_hash: string;
}

export interface PendingLegal {
  all_accepted: boolean;
  pending: PendingLegalDocument[];
}

export interface LegalAcceptance {
  kind: LegalKind;
  version: string;
  accepted_at: string;
}

/**
 * One row of the person's own consent log — what `GET /v1/consumer/legal/acceptances` returns.
 *
 * `kind` is a plain string here, not `LegalKind`, on purpose: this list is HISTORY, and a document
 * type the backend has since retired must still render rather than break the screen. Everything
 * needed to display a row travels with it; nothing is looked up by kind.
 */
export interface ConsentRecord {
  kind: string;
  title: string;
  version: string;
  accepted_at: string;
  /** `null` only for rows recorded before the texts existed — nothing to prove against. */
  content_hash: string | null;
  platform: string | null;
  app_version: string | null;
  /** False for superseded redactions. They stay in the log — that is the point of the log. */
  is_current: boolean;
}

export interface ConsentHistory {
  acceptances: ConsentRecord[];
}

export function fetchConsentHistory(): Promise<ConsentHistory> {
  return apiRequest<ConsentHistory>('/v1/consumer/legal/acceptances');
}

/** Sent with every acceptance so a row in the audit log names the client that produced it. */
export const LEGAL_PLATFORM = 'web';

export function fetchLegalDocument(kind: LegalKind): Promise<LegalDocument> {
  return apiRequest<LegalDocument>(`/v1/legal/documents/${kind}`);
}

/**
 * The current redaction of every required document, WITHOUT the bodies — kind, version, hash.
 *
 * Read by the registration form, which has to send back the hash of the text it offered under each
 * checkbox. Pulling three full documents (tens of KB each) onto a screen where none of them has
 * been opened would be a bad trade; this is one request and a few hundred bytes.
 *
 * Public, like the document endpoint itself: before registration there is no token by definition.
 */
export interface LegalDocumentSummary {
  kind: string;
  version: string;
  title: string;
  content_hash: string;
}

export function fetchCurrentLegalDocuments(): Promise<{ documents: LegalDocumentSummary[] }> {
  return apiRequest<{ documents: LegalDocumentSummary[] }>('/v1/legal/documents');
}

export function fetchPendingLegal(): Promise<PendingLegal> {
  return apiRequest<PendingLegal>('/v1/consumer/legal/pending');
}

export interface LegalAcceptanceBody {
  kind: LegalKind;
  version: string;
  content_hash: string;
  platform: string;
  app_version: string;
}

/**
 * The body of an acceptance, built from the document that was rendered on screen.
 *
 * It takes a `LegalDocument` — the thing with a body — and not a `PendingLegalDocument`,
 * deliberately. `pending` says which version is due; the document response says what the person
 * actually read. When the two disagree (a new redaction published between the two requests) the
 * honest record is the text on screen, and the next `pending` will simply ask again.
 *
 * Split out from the request so the shape can be asserted in a test without a network at all.
 */
export function legalAcceptanceBody(document: {
  kind: LegalKind;
  version: string;
  content_hash: string;
}): LegalAcceptanceBody {
  return {
    kind: document.kind,
    version: document.version,
    content_hash: document.content_hash,
    platform: LEGAL_PLATFORM,
    app_version: APP_VERSION,
  };
}

export function acceptLegalDocument(document: {
  kind: LegalKind;
  version: string;
  content_hash: string;
}): Promise<LegalAcceptance> {
  return apiRequest<LegalAcceptance>('/v1/consumer/legal/acceptances', {
    method: 'POST',
    body: legalAcceptanceBody(document),
  });
}
